import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/dist/query/react";

import * as devConfig from "../app/configs/devConfig";
import { getAuthHeader } from "../app/globalState/GlobalAuthState";
import {
  NotificationCategory,
  NotificationCounts,
  NotificationItem,
  NotificationSettings,
  NotificationView,
} from "../features/notifications/notifications.types";

/**
 * Notifications page, bell drop-down and Home card (backend
 * `api/notification-center`). Always the signed-in person's own notifications.
 */
export const notificationCenterSlice = createApi({
  reducerPath: "notificationCenterSlice",
  tagTypes: ["NotificationCenter", "NotificationSettings"],
  baseQuery: fetchBaseQuery({
    baseUrl: `${devConfig.getServerBaseUrlForEnv()}`,
    prepareHeaders: (headers) => {
      const authHeader = getAuthHeader();
      headers.set("Authorization", authHeader.Authorization);
      return headers;
    },
  }),
  endpoints: (build) => ({
    getNotificationList: build.query<
      NotificationItem[],
      {
        orgId: string;
        view?: NotificationView;
        category?: NotificationCategory | "all";
        search?: string;
        unreadOnly?: boolean;
        before?: string;
        take?: number;
      }
    >({
      query: (params) => ({
        url: "api/notification-center/list",
        params: Object.fromEntries(
          Object.entries(params).filter(
            ([, value]) =>
              value !== undefined && value !== "" && value !== false,
          ),
        ),
      }),
      providesTags: ["NotificationCenter"],
    }),
    getNotificationCounts: build.query<NotificationCounts, { orgId: string }>({
      query: ({ orgId }) => ({
        url: "api/notification-center/counts",
        params: { orgId },
      }),
      providesTags: ["NotificationCenter"],
    }),
    markNotificationsRead: build.mutation<
      number,
      { orgId: string; ids: string[]; value: boolean }
    >({
      query: (body) => ({
        url: "api/notification-center/read",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["NotificationCenter"],
    }),
    markNotificationsDone: build.mutation<
      number,
      { orgId: string; ids: string[]; value: boolean }
    >({
      query: (body) => ({
        url: "api/notification-center/done",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["NotificationCenter"],
    }),
    /** With `returnIds` the server answers {count, ids} (for Undo); older servers a number. */
    markAllNotificationsDone: build.mutation<
      number | { count: number; ids: string[] },
      {
        orgId: string;
        category?: NotificationCategory | "all";
        returnIds?: boolean;
      }
    >({
      query: (body) => ({
        url: "api/notification-center/all-done",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["NotificationCenter"],
    }),
    markWorkNotificationsRead: build.mutation<
      number,
      { orgId: string; workId: string }
    >({
      query: (body) => ({
        url: "api/notification-center/read-work",
        method: "PUT",
        body,
      }),
      invalidatesTags: (count) => (count ? ["NotificationCenter"] : []),
    }),
    deleteNotifications: build.mutation<
      number,
      { orgId: string; ids: string[] }
    >({
      query: (body) => ({
        url: "api/notification-center/delete",
        method: "POST",
        body,
      }),
      invalidatesTags: ["NotificationCenter"],
    }),
    getNotificationSettings: build.query<
      NotificationSettings,
      { orgId: string }
    >({
      query: ({ orgId }) => ({
        url: "api/notification-center/settings",
        params: { orgId },
      }),
      providesTags: ["NotificationSettings"],
    }),
    saveNotificationSettings: build.mutation<
      NotificationSettings,
      NotificationSettings
    >({
      query: (body) => ({
        url: "api/notification-center/settings",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["NotificationSettings"],
    }),
  }),
});

export const {
  useGetNotificationListQuery,
  useLazyGetNotificationListQuery,
  useGetNotificationCountsQuery,
  useMarkNotificationsReadMutation,
  useMarkNotificationsDoneMutation,
  useMarkAllNotificationsDoneMutation,
  useMarkWorkNotificationsReadMutation,
  useDeleteNotificationsMutation,
  useGetNotificationSettingsQuery,
  useSaveNotificationSettingsMutation,
} = notificationCenterSlice;
